import { themeAtom } from "../atoms";
import Dropdown from "./Dropdown/Dropdown";
import WithTooltip from "./WithTooltip";
import {
  ArrowDownTrayIcon,
  ArrowPathIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { useAtomValue } from "jotai";
import { twMerge } from "tailwind-merge";

export type DataGridFilterOption = {
  value: string;
  label: string;
};

type DataGridToolbarProps = {
  searchValue: string;
  onSearchChange: (value: string) => void;
  searchAriaLabel: string;
  filterValue: string;
  onFilterChange: (value: string) => void;
  filterAriaLabel: string;
  allFilterLabel: string;
  filterOptions: DataGridFilterOption[];
  resultCount: number;
  totalCount: number;
  resultLabel: string;
  onReset: () => void;
  resetAriaLabel: string;
  onAdd?: () => void;
  addLabel?: string;
  addDisabled?: boolean;
  onExport?: () => void;
  exportLabel?: string;
  exportDisabled?: boolean;
  className?: string;
};

export default function DataGridToolbar({
  searchValue,
  onSearchChange,
  searchAriaLabel,
  filterValue,
  onFilterChange,
  filterAriaLabel,
  allFilterLabel,
  filterOptions,
  resultCount,
  totalCount,
  resultLabel,
  onReset,
  resetAriaLabel,
  onAdd,
  addLabel = "Add",
  addDisabled = false,
  onExport,
  exportLabel = "Export to CSV",
  exportDisabled = false,
  className,
}: DataGridToolbarProps) {
  const theme = useAtomValue(themeAtom);
  const selectedFilterLabel =
    filterValue === "all"
      ? allFilterLabel
      : filterOptions.find((option) => option.value === filterValue)?.label ?? allFilterLabel;
  const dropdownOptions = [allFilterLabel, ...filterOptions.map((option) => option.label)];

  const selectFilter = (selectedLabel: string) => {
    if (selectedLabel === allFilterLabel) {
      onFilterChange("all");
      return;
    }
    onFilterChange(filterOptions.find((option) => option.label === selectedLabel)?.value ?? "all");
  };

  return (
    <div
      className={twMerge(
        "flex flex-col gap-3 rounded-xl border p-3 lg:flex-row lg:items-center",
        theme.myVesselsTable.tailwind.tableBorder,
        theme.home.tailwind.backgound,
        className,
      )}
    >
      <div className="relative w-full min-w-0 lg:w-[420px] lg:flex-none">
        <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          role="searchbox"
          value={searchValue}
          onChange={(event) => onSearchChange(event.target.value)}
          placeholder="Search across all fields"
          aria-label={searchAriaLabel}
          className={twMerge(
            "h-11 w-full rounded-lg border py-2 pl-10 pr-10 text-sm shadow-sm outline-none transition focus:border-green focus:ring-2 focus:ring-green/15",
            theme.uwhcAssistant.tableRowEven,
            theme.uwhcAssistant.tableInputText,
            theme.myVesselsTable.tailwind.tableBorder,
          )}
        />
        {searchValue && (
          <button
            type="button"
            onClick={() => onSearchChange("")}
            aria-label="Clear search"
            className="absolute right-2 top-1/2 inline-flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-700 dark:hover:text-white"
          >
            <XMarkIcon className="h-4 w-4" aria-hidden="true" />
          </button>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-3 lg:ml-auto lg:justify-end">
        <Dropdown
          className="w-[250px]"
          value={selectedFilterLabel}
          options={dropdownOptions}
          onSelectOption={selectFilter}
          icon={<FunnelIcon className="h-4 w-4" aria-hidden="true" />}
          readOnly
          ariaLabel={filterAriaLabel}
        />
        <span className="whitespace-nowrap rounded-full bg-[#E5F2EF] px-3 py-1.5 text-xs font-semibold text-green dark:bg-teal-950/60 dark:text-teal-200">
          {resultCount} of {totalCount} {resultLabel}
        </span>
        <button
          type="button"
          onClick={onReset}
          title="Reset grid"
          aria-label={resetAriaLabel}
          className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-green transition-colors hover:bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-green/30 dark:hover:bg-gray-900"
        >
          <ArrowPathIcon className="h-5 w-5" aria-hidden="true" />
        </button>
        {onExport && (
          <WithTooltip
            id={"data-grid-" + exportLabel.toLowerCase().replace(/[^a-z0-9]+/g, "-") + "-tooltip"}
            content={exportLabel}
            className="inline-flex shrink-0"
            place="top"
          >
            <button
              type="button"
              onClick={onExport}
              disabled={exportDisabled}
              aria-label={exportLabel}
              className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-green bg-white text-green shadow-sm transition-colors hover:bg-primary-10 focus:outline-none focus-visible:ring-2 focus-visible:ring-green/30 disabled:cursor-not-allowed disabled:opacity-40 dark:bg-gray-800 dark:hover:bg-gray-900"
            >
              <ArrowDownTrayIcon className="h-5 w-5" aria-hidden="true" />
            </button>
          </WithTooltip>
        )}
        {onAdd && (
          <WithTooltip
            id={`data-grid-${addLabel.toLowerCase().replace(/[^a-z0-9]+/g, "-")}-tooltip`}
            content={addLabel}
            className="inline-flex shrink-0"
            place="top"
          >
            <button
              type="button"
              onClick={onAdd}
              disabled={addDisabled}
              aria-label={addLabel}
              className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-green text-white shadow-sm transition-colors hover:bg-[#176a5e] focus:outline-none focus-visible:ring-2 focus-visible:ring-green/30 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <PlusIcon className="h-5 w-5" aria-hidden="true" />
            </button>
          </WithTooltip>
        )}
      </div>
    </div>
  );
}
